import CustomButton from '@/components/atoms/CustomButton';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import { adminRoutes } from '@/constants/routes/app';
import { useAuthContext } from '@/context/auth/useAuthContext';
import useClearOrganizationContext from '@/hooks/super-admin/useClearOrganizationContext';
import useCurrentOrganizationContext from '@/hooks/super-admin/useCurrentOrganizationContext';
import { HStack, Text } from '@chakra-ui/react';
import { useRouterState } from '@tanstack/react-router';
import { format } from 'date-fns';

// The backend applies a super admin's organization viewing context to every app,
// so while it is active Backoffice writes fail with 403 READ_ONLY (MYO-2751).
// Block the Backoffice until the admin ends viewing mode or logs out. The
// Organization Access page is exempt: it is where the context is set and cleared.
const OrganizationViewingModeModal = () => {
  const { data } = useCurrentOrganizationContext();
  const { mutate: clearContext, isPending } = useClearOrganizationContext();
  const { signOut } = useAuthContext();
  const isOnOrganizationAccessPage = useRouterState({
    select: (state) => state.location.pathname.startsWith(adminRoutes.organizationAccess),
  });

  const isOpen = !!data?.organizationId && !isOnOrganizationAccessPage;

  return (
    <ModalContainer
      isOpen={isOpen}
      headerContent={
        <Text variant={'loraSmallTitle'} fontWeight={500} textAlign={'center'} marginBottom={'20px'}>
          Organization viewing mode is active
        </Text>
      }
      overflowY="hidden"
    >
      <Text color={'text.mediumBlue'} fontSize={'18px'} textAlign={'center'}>
        You’re currently viewing{' '}
        <Text as="span" variant={'urbanistSemiBold'} color={'text.darkBlue'}>
          {data?.organizationName}
        </Text>{' '}
        in read-only mode in the client app. While this is active, you can’t make changes in the Backoffice.
      </Text>
      {data?.expiresAt && (
        <Text color={'text.mediumGray'} fontSize={'14px'} textAlign={'center'}>
          Expires automatically at {format(new Date(data.expiresAt), 'dd. MMM h:mm a')}
        </Text>
      )}
      <Text variant={'urbanistSemiBold'} color={'text.darkBlue'} fontSize={'16px'} textAlign={'center'}>
        End viewing mode to continue working in the Backoffice.
      </Text>
      <HStack gap={'16px'} paddingTop={'10px'}>
        <CustomButton
          label="Log out"
          backgroundColor="white"
          labelColor="text.darkBlue"
          onClick={signOut}
          disabled={isPending}
        />
        <CustomButton
          label="End viewing mode"
          backgroundColor="extra.red"
          onClick={clearContext}
          isLoading={isPending}
        />
      </HStack>
    </ModalContainer>
  );
};

export default OrganizationViewingModeModal;
